import { Injectable, signal, computed } from '@angular/core';
import { AgentAuthService } from './agent-auth.service';
import { EncryptionService, session_storage_keys } from './encryption.service';
import {Global} from '../constants/global'

export interface ProductLookupRequest {
  subscriberID: string;
  passKey: string;
  masterID: string;
  companyID: string;
  roleID: string;
  searchContent: string | null;
  fromDate: string | null;
  toDate: string | null;
  pageNumber: number;
  pageSize: number;
  SortOrder: string;
  detailView: boolean;
  pr_CategoryID: string | null;
  pr_RelationID: string | null;
  pr_BrandID: string | null;
  sortOrder: string;
  Pr_ProductID: string;
 }

export interface ProductPriceOption {
  id: string;
  name: string;
  price: number;
  isDefault?: boolean;
}

export interface ApiProductItem {
  productID?: string;
  productId?: string;
  id?: string;
  productCode?: string;
  pr_Code?: string;
  sku?: string;
  barCode?: string;
  barcode?: string;
  productName?: string;
  pr_Name?: string;
  name?: string;
  productDesc?: string;
  description?: string;
  salesPrice?: number | string;
  sellingPrice?: number | string;
  defaultPrice?: number | string;
  price?: number | string;
  unitPrice?: number | string;
  mrpPrice?: number | string;
  mrp?: number | string;
  costPrice?: number | string;
  wholesalePrice?: number | string;
  dealerPrice?: number | string;
  categoryID?: string;
  productCategoryID?: string;
  categoryName?: string;
  productCategoryName?: string;
  brandID?: string;
  productBrandID?: string;
  brandName?: string;
  productBrandName?: string;
  uomID?: string;
  productUomID?: string;
  uomName?: string;
  productUomName?: string;
  hsnID?: string;
  hsnCode?: string;
  totalStock?: number | string;
  stock?: number | string;
  taxRate?: number;
  taxType?: string;
  priceOptions?: ProductPriceOption[];
}

export interface ProductLookupResponse {
  isSuccess: boolean;
  statusCode: number;
  message: string;
  details: string;
  result: any;
}

// Built-in initial seed items with full price options and Finobooks structure
const INITIAL_DYNAMIC_PRODUCTS: ApiProductItem[] = [];

@Injectable({
  providedIn: 'root'
})
export class ProductLookupService {
  private readonly DEFAULT_INV_BASE_URL = 'https://inv.finobooks.com';
  private readonly PRODUCT_STORAGE_KEY = 'fino_cached_products';

  // Signals
  isLoading = signal<boolean>(false);
  lastError = signal<string | null>(null);
  lastSuccessTimestamp = signal<string | null>(null);
  apiProducts = signal<ApiProductItem[]>([]);
  totalCount = signal<number>(0);

  constructor(private agentAuth: AgentAuthService, private encryptionService: EncryptionService,) {
    this.loadCachedProducts();
  }

  /**
   * Dynamically resolves the Inventory API URL from /api/GetProfile/AdminSubscribe subscriberModules.
   * Ensures URL is never prime and defaults to https://inv.finobooks.com.
   */
  getInventoryBaseUrl(): string {
    const profile = this.agentAuth.storedProfileResponse();
    const modules = profile?.result?.subscriberModules || [];
    // Find module for inventory (source containing inv or ID matching inventory module)
    const invMod = modules.find(m => 
      m.moduleSourceControl?.toLowerCase().includes('inv') || 
      (m as any).id?.includes('LI050307150910') ||
      m.moduleID?.includes('LI050307150910')
    );

    if (invMod?.moduleSourceControl) {
      let resolved = invMod.moduleSourceControl.trim().replace(/\/+$/, '');
      if (resolved.toLowerCase().includes('prime')) {
        resolved = this.DEFAULT_INV_BASE_URL;
      }
      return resolved;
    }
    return this.DEFAULT_INV_BASE_URL;
  }

  /**
   * Endpoint for GetProductList: POST /api/LookUp/GetProductList
   */
  getProductListEndpoint(): string {
    const baseUrl = this.getInventoryBaseUrl();
    return `${baseUrl}/api/LookUp/GetProductList`;
  }

  /**
   * Build the standard Finobooks GetProductList payload as specified:
   * {
   *   "subscriberID": "196.88",
   *   "passKey": "...",
   *   "masterID": "ADM-268",
   *   "companyID": "196.7",
   *   "roleID": "MSTADM196.88-1",
   *   "searchContent": null,
   *   "fromDate": null,
   *   "toDate": null,
   *   "pageNumber": 1,
   *   "pageSize": 10,
   *   "SortOrder": "asc",
   *   "detailView": true,
   *   "pr_CategoryID": null,
   *   "pr_RelationID": null,
   *   "pr_BrandID": null,
   *   "sortOrder": "asc"
   * }
   */
  async createPayload(overrides?: Partial<ProductLookupRequest>): Promise<ProductLookupRequest> {
    const login = this.agentAuth.storedResponse();
    const userValues = login?.result?.userValues;
    const activePasskey = this.agentAuth.activePasskey();
    const fallbackPasskey = login?.result?.passkey;
  
    // ✅ Decrypt authentication context from session storage asynchronously
    const [masterRoleId, companyId, masterId, passKey, subscriptionId] = await Promise.all([
      this.encryptionService.decryptAsync<any>(session_storage_keys.DEFAULT_MASTER_ROLE_ID),
      this.encryptionService.decryptAsync<any>(session_storage_keys.DEFAULT_COMPANY_ID),
      this.encryptionService.decryptAsync<any>(session_storage_keys.DEFAULT_MASTER_ID),
      this.encryptionService.decryptAsync<any>(session_storage_keys.DEFAULT_PASS_KEY),
      this.encryptionService.decryptAsync<any>(session_storage_keys.DEFAULT_SUBSCRIPTION_ID),
    ]);
  
    const subscriberIdStr = overrides?.subscriberID || subscriptionId || (login?.result as any)?.subscriberID || (userValues as any)?.subscriptionID?.toString();
    const companyIdStr = overrides?.companyID || companyId || (login?.result?.defaultCompanyId?.toString()) || (userValues as any)?.companys?.[0]?.companyID?.toString();
    const masterIdStr = overrides?.masterID || masterId || this.agentAuth.activeMasterId() || userValues?.userId;
    const roleIdStr = overrides?.roleID || masterRoleId || login?.result?.masterRoleId || userValues?.masterRoleID;
    const resolvedPassKey = overrides?.passKey || passKey || activePasskey || fallbackPasskey;
  
    return {
      subscriberID: subscriberIdStr,
      passKey: resolvedPassKey,
      masterID: masterIdStr,
      companyID: companyIdStr,
      roleID: roleIdStr,
      searchContent: overrides?.searchContent ?? null,
      fromDate: overrides?.fromDate ?? null,
      toDate: overrides?.toDate ?? null,
      pageNumber: overrides?.pageNumber ?? 1,
      pageSize: overrides?.pageSize ?? 10,
      SortOrder: overrides?.SortOrder || 'asc',
      detailView: overrides?.detailView ?? true,
      pr_CategoryID: overrides?.pr_CategoryID ?? null,
      pr_RelationID: overrides?.pr_RelationID ?? null,
      pr_BrandID: overrides?.pr_BrandID ?? null,
      sortOrder: overrides?.sortOrder || 'asc',
      Pr_ProductID: overrides?.Pr_ProductID,
    };
  }

  /**
   * Fetch products dynamically from POST /api/LookUp/GetProductList
   */
  async fetchProductGetByID(params?: Partial<ProductLookupRequest>): Promise<ApiProductItem[]> {
    this.isLoading.set(true);
    this.lastError.set(null);
  
    // ✅ Await the async createPayload method
    const payload = await this.createPayload(params);
  
    try {
      const [module, userName, decryptedToken] = await Promise.all([
        this.encryptionService.decryptAsync<any>(session_storage_keys.API_INV_ORIGIN),
        this.encryptionService.decryptAsync<any>(session_storage_keys.DEFAULT_USER_NAME),
        this.encryptionService.decryptAsync<any>(session_storage_keys.DEFAULT_TOKEN)
      ]);
  
      if (!module?.sourceControl || !module?.accessToken) {
        throw new Error('API_INV_ORIGIN not found in session storage');
      }
  
      const methodPath = 'GetProduct'; 
      const endpoint = `${module.sourceControl}${Global.InvLookUp_EndPoint}${methodPath}`;
  
      let token = decryptedToken || this.agentAuth.activeToken();
      if (!token && typeof window !== 'undefined' && window.localStorage) {
        token = localStorage.getItem('fino_agent_token');
      }
  
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `${module.accessToken || token}`,
        'username': userName ?? '',
        'module-auth': 'true'
      };
  
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload)
      });
  
      let rawData: any;
      if (response.ok) {
        rawData = await response.json();
      } else {
        rawData = {
          isSuccess: false,
          statusCode: response.status,
          message: response.statusText,
          result: []
        };
      }
  
      if (rawData.isSuccess && rawData.result) {
        const items = rawData.result;
        this.apiProducts.set(items);
        this.cacheProducts(items);
        this.lastSuccessTimestamp.set(new Date().toISOString());
        return items;
      }
  
      const cached = this.getCachedProducts();
      if (cached.length > 0) {
        if (payload.searchContent) {
          const q = payload.searchContent.toLowerCase();
          const filtered = cached.filter(p => 
            (p.name && p.name.toLowerCase().includes(q)) ||
            (p.sku && p.sku.toLowerCase().includes(q)) ||
            (p.barcode && p.barcode.toLowerCase().includes(q))
          );
          return filtered;
        }
        this.apiProducts.set(cached);
        return cached;
      }
  
      return INITIAL_DYNAMIC_PRODUCTS;
    } catch (err: any) {
      console.warn('GetProduct fetch warning, loading cached/default products:', err);
      this.lastError.set(err?.message || 'Failed to connect to Inventory API');
      const cached = this.getCachedProducts();
      if (cached.length > 0) {
        if (payload.searchContent) {
          const q = payload.searchContent.toLowerCase();
          return cached.filter(p => 
            (p.name && p.name.toLowerCase().includes(q)) ||
            (p.sku && p.sku.toLowerCase().includes(q)) ||
            (p.barcode && p.barcode.toLowerCase().includes(q))
          );
        }
        this.apiProducts.set(cached);
        return cached;
      }
      return INITIAL_DYNAMIC_PRODUCTS;
    } finally {
      this.isLoading.set(false);
    }
  }
  
  async fetchProductList(params?: Partial<ProductLookupRequest>): Promise<any[]> {
    this.isLoading.set(true);
    this.lastError.set(null);
  
    // ✅ Await the async createPayload method
    const payload = await this.createPayload(params);
  
    try {
      const [module, userName, decryptedToken] = await Promise.all([
        this.encryptionService.decryptAsync<any>(session_storage_keys.API_INV_ORIGIN),
        this.encryptionService.decryptAsync<any>(session_storage_keys.DEFAULT_USER_NAME),
        this.encryptionService.decryptAsync<any>(session_storage_keys.DEFAULT_TOKEN)
      ]);
  
      if (!module?.sourceControl || !module?.accessToken) {
        throw new Error('API_INV_ORIGIN not found in session storage');
      }
  
      const methodPath = 'GetProductList'; 
      const endpoint = `${module.sourceControl}${Global.InvLookUp_EndPoint}${methodPath}`;
  
      let token = decryptedToken || this.agentAuth.activeToken();
      if (!token && typeof window !== 'undefined' && window.localStorage) {
        token = localStorage.getItem('fino_agent_token');
      }
  
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `${module.accessToken || token}`,
        'username': userName ?? '',
        'module-auth': 'true'
      };
  
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload)
      });
  
      let rawData: any;
      if (response.ok) {
        rawData = await response.json();
      } else {
        rawData = {
          isSuccess: false,
          statusCode: response.status,
          message: response.statusText,
          result: []
        };
      }
  
      if (rawData.isSuccess && rawData.result) {
        const items = rawData.result;
        if (items.length > 0) {
          this.apiProducts.set(items);
          this.cacheProducts(items);
          this.lastSuccessTimestamp.set(new Date().toISOString());
          return items;
        }
      }
  
      const cached = this.getCachedProducts();
      if (cached.length > 0) {
        if (payload.searchContent) {
          const q = payload.searchContent.toLowerCase();
          const filtered = cached.filter(p => 
            (p.name && p.name.toLowerCase().includes(q)) ||
            (p.sku && p.sku.toLowerCase().includes(q)) ||
            (p.barcode && p.barcode.toLowerCase().includes(q))
          );
          return filtered;
        }
        this.apiProducts.set(cached);
        return cached;
      }
  
      return INITIAL_DYNAMIC_PRODUCTS;
    } catch (err: any) {
      console.warn('GetProductList fetch warning, loading cached/default products:', err);
      this.lastError.set(err?.message || 'Failed to connect to Inventory API');
      const cached = this.getCachedProducts();
      if (cached.length > 0) {
        if (payload.searchContent) {
          const q = payload.searchContent.toLowerCase();
          return cached.filter(p => 
            (p.name && p.name.toLowerCase().includes(q)) ||
            (p.sku && p.sku.toLowerCase().includes(q)) ||
            (p.barcode && p.barcode.toLowerCase().includes(q))
          );
        }
        this.apiProducts.set(cached);
        return cached;
      }
      return INITIAL_DYNAMIC_PRODUCTS;
    } finally {
      this.isLoading.set(false);
    }
  }
  /**
   * Search or lookup a specific product by barcode or item search text.
  /**
   * Search or lookup a specific product by barcode or item search text.
   * Dynamically calls POST /api/LookUp/GetProductList first with searchContent,
   * then falls back to memory cache and built-in seeds.
   */
  async searchProductByBarcodeOrCode(codeOrBarcode: string): Promise<ApiProductItem | null> {
    const query = codeOrBarcode.trim();
    if (!query) return null;

    // 1. Query dynamic API with searchContent FIRST
    try {
      const results = await this.fetchProductList({
        searchContent: query,
        pageSize: 10
      });

      if (results && results.length > 0) {
        const found = results.find(p => 
          (p.sku && p.sku.toLowerCase() === query.toLowerCase()) ||
          (p.barCode && p.barCode.toLowerCase() === query.toLowerCase()) ||
          (p.barcode && p.barcode.toLowerCase() === query.toLowerCase()) ||
          (p.productCode && p.productCode.toLowerCase() === query.toLowerCase()) ||
          (p.name && p.name.toLowerCase() === query.toLowerCase())
        ) || results[0];
        return found;
      }
    } catch (e) {
      console.warn('Dynamic API search failed, falling back to local cache:', e);
    }

    // 2. Check current loaded products / cache
    const current = this.apiProducts();
    const localMatch = current.find(p => 
      (p.sku && p.sku.toLowerCase() === query.toLowerCase()) ||
      (p.barCode && p.barCode.toLowerCase() === query.toLowerCase()) ||
      (p.barcode && p.barcode.toLowerCase() === query.toLowerCase()) ||
      (p.productCode && p.productCode.toLowerCase() === query.toLowerCase()) ||
      (p.name && p.name.toLowerCase() === query.toLowerCase())
    );

    if (localMatch) {
      return localMatch;
    }

    // 3. Fallback search inside initial seeds
    const seedMatch = INITIAL_DYNAMIC_PRODUCTS.find(p => 
      (p.sku && p.sku.toLowerCase() === query.toLowerCase()) ||
      (p.barcode && p.barcode.toLowerCase() === query.toLowerCase()) ||
      (p.barCode && p.barCode.toLowerCase() === query.toLowerCase()) ||
      (p.name && p.name.toLowerCase().includes(query.toLowerCase()))
    );

    return seedMatch || null;
  }

  /**
   * Converts an ApiProductItem into a full POS Product object with Price Option bindings
   */
  toPosProduct(item: any): any {
    
    const salesPrice = parseFloat(item.salesPrice?.basePrice.toString()  || '0') || 0;
    const mrpPrice = parseFloat(item.salesPrice?.basePrice.toString() || '0') || salesPrice;
    const costPrice = parseFloat(item.salesPrice?.currency.toString() || '0') || (salesPrice * 0.7);
    const wholesalePrice = parseFloat(item.salesPrice?.basePrice?.toString() || '0') || (salesPrice * 0.85);

    const priceOptions: ProductPriceOption[] = item.priceOptions && item.priceOptions.length > 0
      ? item.priceOptions
      : [
          { id: 'sales', name: 'Sales Price', price: salesPrice, isDefault: true },
          { id: 'mrp', name: 'MRP', price: mrpPrice },
          { id: 'wholesale', name: 'Wholesale Tier', price: wholesalePrice },
          { id: 'cost', name: 'Cost Price', price: costPrice }
        ];

    const sku =  item.pr_ProductCode || item.barcode || 'PRD-001';
    const barcode =  item?.barCode || sku;

    return {
      item,
      sku: sku,
      barcode: barcode,
      pr_ProductID: item?.pr_ProductID,
      pr_ProductName: item?.pr_ProductName || item?.productName,
      price: salesPrice,
      salesPrice: salesPrice,
      mrpPrice: mrpPrice,
      wholesalePrice: wholesalePrice,
      costPrice: costPrice,
      priceOptions: priceOptions,
      selectedPriceOption: 'sales',
      rep: '000301',
      category: item.categoryName || 'General',
      categoryId: item?.productCategory?.categoryID || 'cat-gen',
      categoryName: item?.productCategory?.categoryName || 'General',
      brandId: item?.productBrand?.brandID || 'brand-gen',
      brandName: item?.productBrand?.brandName || 'General Brand',
      uomId: item.uomID || 'uom-ea',
      uomName: item.uomName || 'ea',
      hsnId: item.hsnID || 'HSN-1',
      hsnCode: item.hsnCode || 'HSN-GEN',
      productDesc: item.productDesc || '',
      remarks: '',
      totalStock: item.totalStock !== undefined ? Number(item.totalStock) : 100
    };
  }

  /**
   * Normalizes disparate API product item fields into a clean structure
   */
  normalizeProductList(resultData: any): ApiProductItem[] {
    let list: any[] = [];
    if (Array.isArray(resultData)) {
      list = resultData;
    } else if (Array.isArray(resultData?.items)) {
      list = resultData.items;
    } else if (Array.isArray(resultData?.data)) {
      list = resultData.data;
    } else if (Array.isArray(resultData?.list)) {
      list = resultData.list;
    } else if (Array.isArray(resultData?.products)) {
      list = resultData.products;
    } else if (Array.isArray(resultData?.table)) {
      list = resultData.table;
    } else if (Array.isArray(resultData?.table1)) {
      list = resultData.table1;
    } else if (resultData && typeof resultData === 'object') {
      const arrKey = Object.keys(resultData).find(k => Array.isArray(resultData[k]));
      if (arrKey) {
        list = resultData[arrKey];
      }
    }

    return list.map((item: any, idx: number) => {
      const sku = item.productCode || item.pr_Code || item.sku || item.barCode || item.barcode || item.pr_Barcode || `PRD-${idx + 1}`;
      const name = item.productName || item.pr_Name || item.name || 'Unnamed Product';
      
      // Parse prices
      const salesPrice = parseFloat(item.salesPrice || item.pr_SalesPrice || item.sellingPrice || item.price || item.defaultPrice || item.unitPrice || 0) || 0;
      const mrpPrice = parseFloat(item.mrpPrice || item.pr_MRPPrice || item.mrp || 0) || salesPrice;
      const costPrice = parseFloat(item.costPrice || item.pr_CostPrice || 0) || (salesPrice * 0.7);
      const wholesalePrice = parseFloat(item.wholesalePrice || item.pr_WholesalePrice || item.dealerPrice || 0) || (salesPrice * 0.85);

      // Price options list
      const priceOptions: ProductPriceOption[] = [
        { id: 'sales', name: 'Sales Price', price: salesPrice, isDefault: true },
        { id: 'mrp', name: 'MRP', price: mrpPrice },
        { id: 'wholesale', name: 'Wholesale Tier', price: wholesalePrice },
        { id: 'cost', name: 'Cost Price', price: costPrice }
      ];

      return {
        productID: item.productID || item.productId || item.id || `PRD-ID-${idx}`,
        sku: sku,
        productCode: sku,
        barcode: item.barCode || item.barcode || item.pr_Barcode || sku,
        barCode: item.barCode || item.barcode || item.pr_Barcode || sku,
        name: name,
        productName: name,
        productDesc: item.productDesc || item.pr_Description || item.description || '',
        salesPrice: salesPrice,
        price: salesPrice,
        mrpPrice: mrpPrice,
        costPrice: costPrice,
        wholesalePrice: wholesalePrice,
        priceOptions: priceOptions,
        categoryID: item.categoryID || item.productCategoryID || item.pr_CategoryID || 'cat-gen',
        categoryName: item.categoryName || item.productCategoryName || item.pr_CategoryName || item.category || 'General',
        brandID: item.brandID || item.productBrandID || item.pr_BrandID || 'brand-gen',
        brandName: item.brandName || item.productBrandName || item.pr_BrandName || 'General Brand',
        uomID: item.uomID || item.productUomID || item.pr_UOMID || 'uom-ea',
        uomName: item.uomName || item.productUomName || item.pr_UOMName || 'ea',
        hsnCode: item.hsnCode || item.pr_HsnCode || 'HSN-GEN',
        hsnID: item.hsnID || item.pr_HsnID || 'HSN-1',
        totalStock: parseInt(item.totalStock || item.stock || item.currentStock || item.qty || 100, 10)
      };
    });
  }

  private cacheProducts(items: ApiProductItem[]) {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(this.PRODUCT_STORAGE_KEY, JSON.stringify(items));
      }
    } catch {
      // Storage quota or disabled
    }
  }

  private getCachedProducts(): ApiProductItem[] {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const saved = localStorage.getItem(this.PRODUCT_STORAGE_KEY);
        if (saved) {
          return JSON.parse(saved);
        }
      }
    } catch {
      // Ignore
    }
    return INITIAL_DYNAMIC_PRODUCTS;
  }

  private loadCachedProducts() {
    const cached = this.getCachedProducts();
    if (cached.length > 0) {
      this.apiProducts.set(cached);
    } else {
      this.apiProducts.set(INITIAL_DYNAMIC_PRODUCTS);
      this.cacheProducts(INITIAL_DYNAMIC_PRODUCTS);
    }
  }
}
